import { Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  isBuyerEmailValid,
  isFullNameValid,
  type OfferPortalWizardContext,
} from 'state_machines/OfferPortalWizardMachine';

import { useFieldErrors } from './useFieldErrors';
import {
  CardHeading,
  FIELD_CARD,
  FIELD_INPUT,
  FIELD_LABEL,
  FieldError,
  type Send,
} from './wizardFields';

/** Step 3 of 6 of the offer wizard — the buyer info the purchase contract needs. */
const DataForDocumentsStep = ({
  context,
  send,
}: {
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const { shouldShowError, onBlur } = useFieldErrors(context.showError);
  const nameViolation = context.fieldViolations['buyerName'];
  const emailViolation = context.fieldViolations['buyerEmail'];
  const nameInvalid =
    (shouldShowError('buyer-name') && !isFullNameValid(context.buyerFullName)) ||
    nameViolation != null;
  const emailInvalid =
    (shouldShowError('buyer-email') && !isBuyerEmailValid(context.buyerEmail)) ||
    emailViolation != null;
  return (
    <>
      <Col className={FIELD_CARD}>
        <CardHeading title="Buyer details" />
        <Col className="gap-2">
          <Text as="label" htmlFor="buyer-name" className={FIELD_LABEL}>
            Full name
          </Text>
          <Input
            id="buyer-name"
            type="text"
            value={context.buyerFullName}
            onChange={(event) => send({ type: 'SET_BUYER_FULL_NAME', value: event.target.value })}
            onBlur={onBlur('buyer-name')}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Full name"
            aria-required="true"
            aria-invalid={nameInvalid}
            error={nameInvalid}
            analyticsName="cosmos-make-offer-buyer-name"
          />
          {nameViolation != null ? <FieldError>{nameViolation}</FieldError> : null}
          {shouldShowError('buyer-name') && !isFullNameValid(context.buyerFullName) ? (
            <FieldError>Enter the buyer’s full name.</FieldError>
          ) : null}
        </Col>
        <Col className="gap-2">
          <Text as="label" htmlFor="buyer-email" className={FIELD_LABEL}>
            Email address
          </Text>
          <Input
            id="buyer-email"
            type="email"
            value={context.buyerEmail}
            onChange={(event) => send({ type: 'SET_BUYER_EMAIL', value: event.target.value })}
            onBlur={onBlur('buyer-email')}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Email address"
            aria-invalid={emailInvalid}
            error={emailInvalid}
            analyticsName="cosmos-make-offer-buyer-email"
          />
          {emailViolation != null ? <FieldError>{emailViolation}</FieldError> : null}
          {shouldShowError('buyer-email') && !isBuyerEmailValid(context.buyerEmail) ? (
            <FieldError>Check if this email address is correct.</FieldError>
          ) : null}
        </Col>
      </Col>
      <Row className="items-start gap-3 rounded-[12px] bg-od-bg-accent-brand/10 p-4">
        <IconInfoCircle className="size-5 shrink-0 text-od-content-accent-brand" />
        <Col className="gap-1">
          <Text className="text-body-sm font-medium text-od-content-primary">
            Buyer details are used for the purchase contract
          </Text>
          <Text className="text-body-sm text-od-content-secondary">
            The listing partner will use these details to prepare the contract after reviewing your
            offer.
          </Text>
        </Col>
      </Row>
    </>
  );
};

export default DataForDocumentsStep;
